<template>
  <div class="rankings">
    <div class="container">
      <!-- Header -->
      <header class="rk-head reveal">
        <p class="eyebrow">Le palmarès de la banane</p>
        <h1 class="rk-title">Classement</h1>
        <SortTabs v-model="board" :options="boards" />
      </header>

      <!-- Board intro -->
      <section class="rk-intro reveal" :class="`rk-intro--${board.toLowerCase()}`">
        <div class="rk-intro__emoji">{{ current.emoji }}</div>
        <div class="rk-intro__copy">
          <h2>{{ current.heading }}</h2>
          <p>{{ current.blurb }}</p>
        </div>
      </section>

      <!-- Loading -->
      <div v-if="loading && !jokes.length" class="rk-state">
        <span class="rk-spin">🍌</span>
        <p>On épluche le classement…</p>
      </div>

      <!-- Empty -->
      <div v-else-if="!jokes.length" class="rk-state">
        <div class="rk-state__emoji">🫥</div>
        <p>Rien à afficher ici pour l'instant.</p>
        <BananaButton to="/" variant="outline" size="sm">Retour à l'accueil</BananaButton>
      </div>

      <!-- Ranked list -->
      <ol v-else class="rk-list">
        <li
          v-for="(joke, i) in jokes"
          :key="joke.id"
          class="rk-item reveal"
          :style="{ animationDelay: (Math.min(i, 8) * 0.04) + 's' }"
        >
          <div class="rk-rank" :class="medalClass(i)">
            <span v-if="i < 3" class="rk-rank__medal">{{ medals[i] }}</span>
            <span class="rk-rank__num">{{ i + 1 }}</span>
          </div>
          <div class="rk-card">
            <JokeCard :joke="joke" />
          </div>
        </li>
      </ol>

      <!-- Load more -->
      <div v-if="jokes.length && canLoadMore" class="rk-more">
        <BananaButton
          variant="dark"
          size="lg"
          :loading="loadingMore"
          @click="loadMore"
        >
          Voir plus de {{ current.short }}
        </BananaButton>
      </div>
      <p v-else-if="jokes.length && !canLoadMore" class="rk-end">
        {{ current.emoji }} Fin du classement — t'as tout vu.
      </p>
    </div>
  </div>
</template>

<script>
import JokeCard from '@/components/JokeCard.vue'
import BananaButton from '@/components/ui/BananaButton.vue'
import SortTabs from '@/components/SortTabs.vue'
import { JOKES_QUERY } from '@/graphql/jokes'

const PAGE = 30

const BOARD_META = {
  TOP: {
    emoji: '🏆',
    heading: 'Le Top — les plus aimées',
    blurb: 'Les blagues qui vous font le plus marrer aujourd\'hui. Le score, c\'est les pouces en l\'air moins les pouces en bas. Plus c\'est haut, plus c\'est culte.',
    short: 'championnes',
  },
  FLOP: {
    emoji: '💩',
    heading: 'Les Méga Bides — le mur de la honte',
    blurb: 'Le club des blagues les plus détestées, comme au bon vieux temps de labanane.org. Elles ont osé, vous avez sanctionné. On les affiche quand même, parce qu\'un bide, ça se célèbre.',
    short: 'bides',
  },
  TOP_LEGACY: {
    emoji: '👑',
    heading: 'Les Légendes — le palmarès d\'origine',
    blurb: 'Le classement figé, hérité du site original. Ces scores légendaires ont été accumulés à l\'époque par la communauté de labanane.org — ils sont gravés dans le marbre et ne bougent plus.',
    short: 'légendes',
  },
  FLOP_LEGACY: {
    emoji: '💀',
    heading: 'Les Bides d\'Antan — le cimetière d\'origine',
    blurb: 'Les blagues qui ont le plus fait un four à l\'époque de labanane.org. Ce classement figé, hérité du site original, exhume les pires bides de l\'histoire — sanctionnés jadis par la communauté, gravés à jamais dans le marbre.',
    short: 'bides d\'antan',
  },
}

export default {
  name: 'RankingsView',
  components: { JokeCard, BananaButton, SortTabs },
  data() {
    return {
      board: 'TOP',
      jokes: [],
      loading: false,
      loadingMore: false,
      canLoadMore: true,
      medals: ['🥇', '🥈', '🥉'],
      boards: [
        { value: 'TOP', label: 'Top', emoji: '🏆' },
        { value: 'FLOP', label: 'Méga bides', emoji: '💩' },
        { value: 'TOP_LEGACY', label: 'Légendes', emoji: '👑' },
        { value: 'FLOP_LEGACY', label: 'Bides d\'antan', emoji: '💀' },
      ],
    }
  },
  computed: {
    current() {
      return BOARD_META[this.board]
    },
  },
  watch: {
    board() {
      this.fetchBoard()
    },
  },
  mounted() {
    this.fetchBoard()
  },
  methods: {
    medalClass(i) {
      return ['rk-rank--gold', 'rk-rank--silver', 'rk-rank--bronze'][i] || ''
    },
    fetchBoard() {
      this.loading = true
      this.canLoadMore = true
      this.jokes = []
      this.$apollo
        .query({
          query: JOKES_QUERY,
          fetchPolicy: 'network-only',
          variables: { orderBy: this.board, count: PAGE, offset: 0 },
        })
        .then((res) => {
          const list = (res.data && res.data.jokes) || []
          this.jokes = list
          this.canLoadMore = list.length === PAGE
        })
        .catch((err) => {
          console.error('Rankings fetch failed:', err)
          this.jokes = []
          this.canLoadMore = false
        })
        .finally(() => {
          this.loading = false
        })
    },
    loadMore() {
      if (this.loading || this.loadingMore || !this.canLoadMore) return
      this.loadingMore = true
      const boardAtRequest = this.board
      this.$apollo
        .query({
          query: JOKES_QUERY,
          fetchPolicy: 'network-only',
          variables: { orderBy: this.board, count: PAGE, offset: this.jokes.length },
        })
        .then((res) => {
          if (boardAtRequest !== this.board) return
          const list = (res.data && res.data.jokes) || []
          this.jokes = this.jokes.concat(list)
          this.canLoadMore = list.length === PAGE
        })
        .catch((err) => {
          console.error('Rankings load-more failed:', err)
          this.canLoadMore = false
        })
        .finally(() => {
          this.loadingMore = false
        })
    },
  },
}
</script>

<style scoped>
.rankings {
  padding: 40px 0 80px;
}

/* Header */
.rk-head {
  text-align: center;
  margin-bottom: 32px;
}
.rk-title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: clamp(2.4rem, 6vw, 3.6rem);
  color: var(--ink-900);
  margin: 4px 0 22px;
  letter-spacing: -0.02em;
}
.rk-head :deep(.tabs),
.rk-head :deep(.sort-tabs) {
  justify-content: center;
  max-width: 100%;
}
/* On mobile SortTabs switches to icons-only, full-width — let it drive its own
   layout (don't force-center, which fought the space-between distribution). */
@media (max-width: 560px) {
  .rk-head :deep(.tabs),
  .rk-head :deep(.sort-tabs) {
    display: flex;
    width: 100%;
  }
}

/* Intro banner */
.rk-intro {
  display: flex;
  align-items: center;
  gap: 18px;
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-lg, 22px);
  box-shadow: var(--shadow-pop);
  padding: 20px 24px;
  margin-bottom: 34px;
}
.rk-intro--top { background: linear-gradient(120deg, var(--banana-100), var(--surface)); }
.rk-intro--flop { background: linear-gradient(120deg, #fbe3da, var(--surface)); }
.rk-intro--top_legacy { background: linear-gradient(120deg, var(--banana-200), var(--surface)); }
.rk-intro--flop_legacy { background: linear-gradient(120deg, #d9cbc4, var(--surface)); }
.rk-intro__emoji {
  font-size: 2.8rem;
  line-height: 1;
  flex-shrink: 0;
  filter: drop-shadow(2px 2px 0 rgba(0, 0, 0, 0.15));
}
.rk-intro__copy h2 {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.4rem;
  color: var(--ink-900);
  margin: 0 0 4px;
}
.rk-intro__copy p {
  font-family: var(--font-body);
  color: var(--ink-500);
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.5;
}

/* States */
.rk-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--ink-500);
  font-family: var(--font-accent);
  font-style: italic;
  font-size: 1.15rem;
}
.rk-state__emoji { font-size: 3rem; margin-bottom: 8px; }
.rk-state p { margin: 0 0 16px; }
.rk-spin {
  display: inline-block;
  font-size: 2.4rem;
  animation: rk-spin 1.1s linear infinite;
}
@keyframes rk-spin { to { transform: rotate(360deg); } }

/* Ranked list */
.rk-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.rk-item {
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 16px;
  align-items: start;
}

/* Rank badge */
.rk-rank {
  position: sticky;
  top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: var(--cream);
  border: var(--border-w) solid var(--border-c);
  border-radius: 50%;
  box-shadow: var(--shadow-pop);
  font-family: var(--font-display);
  font-weight: 800;
}
.rk-rank__medal { font-size: 1.5rem; line-height: 1; }
.rk-rank__num {
  font-size: 1.15rem;
  color: var(--ink-900);
  line-height: 1;
}
.rk-rank:not(.rk-rank--gold):not(.rk-rank--silver):not(.rk-rank--bronze) .rk-rank__num {
  font-size: 1.35rem;
}
.rk-rank--gold {
  background: linear-gradient(160deg, #ffe27a, #f5b301);
  transform: scale(1.06);
}
.rk-rank--gold .rk-rank__num { color: #6b4a00; font-size: 0.95rem; }
.rk-rank--silver {
  background: linear-gradient(160deg, #f4f4f4, #c9cdd2);
}
.rk-rank--silver .rk-rank__num { color: #4a4f55; font-size: 0.95rem; }
.rk-rank--bronze {
  background: linear-gradient(160deg, #f0c088, #cd7f32);
}
.rk-rank--bronze .rk-rank__num { color: #5c3410; font-size: 0.95rem; }

.rk-card { min-width: 0; }

/* Load more / end */
.rk-more {
  display: flex;
  justify-content: center;
  margin-top: 40px;
}
.rk-end {
  text-align: center;
  margin-top: 40px;
  font-family: var(--font-accent);
  font-style: italic;
  font-size: 1.1rem;
  color: var(--ink-500);
}

@media (max-width: 640px) {
  .rk-item {
    grid-template-columns: 48px 1fr;
    gap: 10px;
  }
  .rk-rank {
    width: 48px;
    height: 48px;
    top: 14px;
  }
  .rk-rank__medal { font-size: 1.15rem; }
  .rk-rank__num { font-size: 1rem; }
  .rk-intro { flex-direction: column; text-align: center; gap: 10px; }
}
</style>
